import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';
import { AuthenticationService } from '../../services/authentication.service';
import { CommunicationService } from '../../services/communication.service';
import { publicMethods } from '../../globals';
import {
  isTaxProfilePlay, taxAccountsSourceForPeriod, taxExternalUrl, taxProfileArticles, taxProfileFacts, taxProfilePeople,
  taxProfilePeriodLabel, TAX_PROFILE_PLAYS, TaxProfileArticle, TaxProfileFact, TaxProfilePerson, TaxProfilePlay,
} from './tax-profile-presenters';
import {
  TaxEnergyTab, TaxGrantRecord, TaxPatentRecord, TaxProfileHistory, taxEnergyHistory, taxGrantHistory, taxPatentHistory,
} from './tax-profile-history';
import { TaxInsightBlock, TaxInsightGroup, TaxInsightPlays, taxInsightPlays } from './tax-profile-insights';
import { companyProfileSafeUrl } from '../company-profile-template/company-profile-contact-links.component';
import { companyProfileOfferingItems } from '../company-profile-template/company-profile-offerings.component';
import { financialPeriodsText, financialReportPeriodCount, hasFinancialReport } from '../company-profile-template/financial-report';
import { KeyFigureAdviser, KeyFigureStrip } from '../company-profile-template/key-figures';
import { hasTaxKeyFigures, taxAdvisers, taxKeyFigures } from './tax-key-figures';
import { CompanyProfileInsight, formatInsightDate, insightConfidenceColour, insightConfidenceLabel } from '../company-profile-template/profile-insight';

/** loading → ready | error | unavailable (the plan has no Tax mode access to Magic Insights). */
export type TaxInsightsState = 'loading' | 'ready' | 'error' | 'unavailable';

interface TaxLandAddress {
  title_number?: string;
  tenure?: string;
  classification?: string;
  price_paid?: number;
  date_proprietor_added?: string;
  address?: Record<string, string | null>;
  epc_data?: Array<{
    report_type?: string;
    property_type?: string;
    asset_rating?: number;
    asset_rating_band?: string;
    floor_area?: number;
    lodgement_date?: string;
  }>;
}

@Component({
  selector: 'tax-accountancy-company-profile',
  templateUrl: './tax-accountancy-company-profile.component.html',
  styleUrls: ['./tax-accountancy-company-profile.component.css'],
  standalone: false,
})
export class TaxAccountancyCompanyProfileComponent implements OnInit, OnDestroy {
  readonly plays = TAX_PROFILE_PLAYS;
  readonly periodLabel = taxProfilePeriodLabel;
  private readonly destroyed$ = new Subject<void>();
  companyNumber = '';
  company: Record<string, any> = {};
  selectedPlay: TaxProfilePlay = 'rd';
  /** The "Key figures for this relief" strip for each service, from the same plan-gated data as the evidence cards. */
  keyFiguresByPlay: Record<TaxProfilePlay, KeyFigureStrip | null> = {
    rd: null, capital_allowances: null, accounts_audit: null, energy: null, international_tax: null,
  };
  /** Accountant, auditor and banker for the advisers panel. */
  advisers: KeyFigureAdviser[] = [];
  factsByPlay: Record<TaxProfilePlay, TaxProfileFact[]> = {
    rd: [], capital_allowances: [], accounts_audit: [], energy: [], international_tax: [],
  };
  articles: TaxProfileArticle[] = [];
  people: TaxProfilePerson[] = [];
  showAllPeople = false;
  loading = true;
  loadingError = false;
  hasMagicInsightsAccess = false;
  hasPatentAccess = false;
  hasEnergyAccess = false;
  hasPAFAccess = false;
  landOwnershipData: TaxLandAddress[] = [];
  landOwnershipLoading = false;
  private landRequestedFor = '';
  private landRequestToken = 0;
  savingDefault = false;
  defaultSaved = false;
  insightPlays: TaxInsightPlays = {};
  insightsState: TaxInsightsState = 'loading';
  selectedInsight: CompanyProfileInsight | null = null;
  selectedInsightGroup: TaxInsightGroup | null = null;
  private readonly insightRequests$ = new Subject<string>();
  readonly confidenceLabel = insightConfidenceLabel;
  readonly confidenceColour = insightConfidenceColour;
  readonly formatInsightDate = formatInsightDate;
  hasFinancialReport = false;
  financialPeriodCount = 0;
  readonly financialPeriodsText = financialPeriodsText;
  financialReportOpen = false;
  financialTab: string | null = null;
  /** The recorded history dialog that is open, if any, and its records. */
  historyOpen: TaxProfileHistory | null = null;
  historyPatents: TaxPatentRecord[] = [];
  historyGrants: TaxGrantRecord[] = [];
  historyEnergy: TaxEnergyTab[] = [];
  /** The profile data with plan-gated sections emptied, as the evidence cards and history dialogs see it. */
  private visibleCompany: Record<string, any> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private authenticationService: AuthenticationService,
    private communicationService: CommunicationService,
    private titleService: Title,
    private pubMethods: publicMethods,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      const play = params.get('play');
      this.selectedPlay = isTaxProfilePlay(play) ? play : 'rd';
    });
    this.communicationService.getHasAIRAAccess().pipe(takeUntil(this.destroyed$))
      .subscribe(access => this.hasMagicInsightsAccess = !!access);
    this.communicationService.getHasPatentsAccessMessage().pipe(takeUntil(this.destroyed$))
      .subscribe(access => {
        this.hasPatentAccess = !!access;
        this.rebuildFacts();
      });
    this.communicationService.getHasEnergyAndEmissionsAccessMessage().pipe(takeUntil(this.destroyed$))
      .subscribe(access => {
        this.hasEnergyAccess = !!access;
        this.rebuildFacts();
      });
    this.communicationService.getHasPAFAccess().pipe(takeUntil(this.destroyed$))
      .subscribe(access => {
        this.hasPAFAccess = !!access;
        if (!this.hasPAFAccess) {
          this.landRequestToken++;
          this.landRequestedFor = '';
          this.landOwnershipData = [];
          this.landOwnershipLoading = false;
        } else {
          this.loadLandOwnershipIfPermitted();
        }
      });
    // Magic Insights load beside the profile data so a slow answer set never holds up the page.
    this.insightRequests$.pipe(
      tap(() => {
        this.insightPlays = {};
        this.insightsState = 'loading';
        this.selectedInsight = null;
        this.selectedInsightGroup = null;
      }),
      switchMap(number => number
        ? this.companyService.getTaxProfileInsights(number).pipe(
          map(data => ({ data, status: 0 })),
          catchError(error => of({ data: null, status: Number(error?.status) || -1 })))
        : of({ data: null, status: -1 })),
      takeUntil(this.destroyed$),
    ).subscribe(({ data, status }) => {
      this.insightPlays = taxInsightPlays(data);
      this.insightsState = status === 403 ? 'unavailable' : status ? 'error' : 'ready';
    });
    this.route.paramMap.pipe(
      map(params => params.get('id') || ''),
      distinctUntilChanged(),
      tap(number => {
        this.companyNumber = number;
        this.company = {};
        this.articles = [];
        this.people = [];
        this.landRequestToken++;
        this.landRequestedFor = '';
        this.landOwnershipData = [];
        this.landOwnershipLoading = false;
        this.showAllPeople = false;
        this.financialReportOpen = false;
        this.financialTab = null;
        this.closeHistory();
        this.visibleCompany = {};
        this.hasFinancialReport = false;
        this.financialPeriodCount = 0;
        this.factsByPlay = { rd: [], capital_allowances: [], accounts_audit: [], energy: [], international_tax: [] };
        this.keyFiguresByPlay = { rd: null, capital_allowances: null, accounts_audit: null, energy: null, international_tax: null };
        this.advisers = [];
        this.loading = true;
        this.loadingError = false;
        this.insightRequests$.next(number);
      }),
      switchMap(number => number
        ? this.companyService.getCompanyProfileData(number).pipe(catchError(() => of(null)))
        : of(null)),
      takeUntil(this.destroyed$),
    ).subscribe(data => {
      this.company = data?.company || {};
      this.loadingError = !this.company.company_overview;
      this.loading = false;
      if (this.loadingError) return;
      this.articles = taxProfileArticles(this.company);
      this.people = taxProfilePeople(this.company);
      this.hasFinancialReport = hasFinancialReport(this.company.financials?.accounts, this.company.asset_breakdown);
      this.financialPeriodCount = financialReportPeriodCount(this.company.financials?.accounts, this.company.asset_breakdown);
      this.rebuildFacts();
      this.loadLandOwnershipIfPermitted();
      this.titleService.setTitle((this.company.company_overview.name || 'Company') + ' | Zint Tax & Accountancy');
    });
  }

  private loadLandOwnershipIfPermitted(): void {
    if (!this.hasPAFAccess || !this.companyNumber || !this.company?.company_overview ||
      this.landRequestedFor === this.companyNumber) return;
    const number = this.companyNumber;
    const token = ++this.landRequestToken;
    this.landRequestedFor = number;
    this.landOwnershipLoading = true;
    this.companyService.getLandRegistryAddresses(number).pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: addresses => {
          if (token !== this.landRequestToken || !this.hasPAFAccess || this.companyNumber !== number) return;
          this.landOwnershipData = Array.isArray(addresses) ? addresses.map(address => ({
            ...address,
            epc_data: Array.isArray(address?.epc_data) ? address.epc_data : [],
          })) : [];
          this.landOwnershipLoading = false;
        },
        error: () => {
          if (token !== this.landRequestToken || !this.hasPAFAccess || this.companyNumber !== number) return;
          this.landOwnershipData = [];
          this.landOwnershipLoading = false;
        },
      });
  }

  landAddressText(address: TaxLandAddress): string {
    return Object.values(address.address || {}).filter(Boolean).join(', ') || 'Address not recorded';
  }

  private rebuildFacts(): void {
    if (!this.company?.company_overview) return;
    const visible = this.visibleCompany = {
      ...this.company,
      patents_filed: this.hasPatentAccess ? this.company.patents_filed : [],
      energy_and_emissions: this.hasEnergyAccess ? this.company.energy_and_emissions : {},
    };
    for (const play of this.plays) {
      this.factsByPlay[play.id] = taxProfileFacts(visible, play.id);
      this.keyFiguresByPlay[play.id] = taxKeyFigures(visible, play.id);
    }
    this.advisers = taxAdvisers(visible);
    // Losing an entitlement closes the dialog that showed it.
    if ((this.historyOpen === 'patents' && !this.hasPatentAccess) || (this.historyOpen === 'energy' && !this.hasEnergyAccess)) {
      this.closeHistory();
    }
  }

  retryInsights(): void {
    this.insightRequests$.next(this.companyNumber);
  }

  get activeInsightGroups(): TaxInsightGroup[] {
    return this.insightPlays[this.selectedPlay]?.groups || [];
  }

  get activeInsightBlocks(): TaxInsightBlock[] {
    return this.insightPlays[this.selectedPlay]?.blocks || [];
  }

  /** Whether the team's plan includes any Magic Insights questions for the selected service. */
  get activeInsightQuestionCount(): number {
    return this.insightPlays[this.selectedPlay]?.questionCount || 0;
  }

  insightCount(play: TaxProfilePlay): number {
    return this.insightPlays[play]?.groups.length || 0;
  }

  sourceNote(group: TaxInsightGroup): string {
    return group.referenceCount === 1 ? '1 cited source' : group.referenceCount ? `${group.referenceCount} cited sources` : 'No sources cited';
  }

  openInsight(group: TaxInsightGroup, insight?: CompanyProfileInsight): void {
    this.selectedInsightGroup = group;
    this.selectedInsight = insight || group.cards[0] || null;
  }

  closeInsight(): void {
    this.selectedInsight = null;
    this.selectedInsightGroup = null;
  }

  // The shared dialog moves focus to its close button, keeps Tab inside itself, closes on Escape or a backdrop click and
  // returns focus to the button that opened it.
  /** Opens the financial report, at the given tab when one is named (e.g. from a key figure). */
  openFinancialReport(tab?: string): void {
    if (!this.hasFinancialReport) return;
    if (tab) this.financialTab = tab;
    this.financialReportOpen = true;
  }

  /** A key figure's own action: the energy figure opens the energy and emissions history. */
  onKeyFigureAction(action: string): void {
    if (action === 'energy-history' && this.hasEnergyAccess) this.openHistory('energy');
  }

  closeFinancialReport(): void {
    this.financialReportOpen = false;
  }

  /** Opens the full record behind a recorded data card: every patent, every grant, or energy and emissions by period. */
  openHistory(history: TaxProfileHistory): void {
    const company = this.visibleCompany;
    if (!company?.['company_overview']) return;
    this.historyPatents = history === 'patents' ? taxPatentHistory(company) : [];
    this.historyGrants = history === 'grants' ? taxGrantHistory(company) : [];
    this.historyEnergy = history === 'energy'
      ? taxEnergyHistory(company, period => taxAccountsSourceForPeriod(company, period)) : [];
    this.historyOpen = history;
  }

  closeHistory(): void {
    this.historyOpen = null;
    this.historyPatents = [];
    this.historyGrants = [];
    this.historyEnergy = [];
  }

  get historyHeading(): string {
    return this.historyOpen === 'patents' ? 'Patent filings' : this.historyOpen === 'grants' ? 'Grant projects' : 'Energy and emissions';
  }

  get historyIntro(): string {
    return this.historyOpen === 'patents' ? `${this.countText(this.historyPatents.length, 'recorded patent')}, newest filing first.`
      : this.historyOpen === 'grants' ? `${this.countText(this.historyGrants.length, 'recorded grant')}, newest project first.`
        : 'Figures reported in the filed accounts, newest period first.';
  }

  private countText(count: number, noun: string): string {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  selectPlay(play: TaxProfilePlay): void {
    if (play === this.selectedPlay) return;
    this.selectedPlay = play;
    // Keep ?play= in the URL so links and refreshes open this view, but switching views is not a
    // page change: replace the history entry so Back leaves the profile rather than stepping through
    // views, and opt out of the router's scroll-to-top so the reader keeps their place on the page.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { play },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      scroll: 'manual',
    });
  }

  get selectedPlayLabel(): string {
    return this.plays.find(play => play.id === this.selectedPlay)?.label || 'Tax and accountancy';
  }

  get activeKeyFigures(): KeyFigureStrip | null {
    return this.keyFiguresByPlay[this.selectedPlay];
  }

  get hasActiveKeyFigures(): boolean {
    return hasTaxKeyFigures(this.activeKeyFigures);
  }

  get visibleFacts(): TaxProfileFact[] {
    return this.factsByPlay[this.selectedPlay];
  }

  get latestAccountsPeriod(): string {
    return this.company?.financials?.made_up_to_dates?.[0] || '';
  }

  get latestAccountsSubmitted(): string {
    const accounts = this.company?.financials?.accounts || {};
    const section: any = Object.values(accounts).find((item: any) => item?.columns?.length);
    return section?.columns?.[0]?.submitted || '';
  }

  get productsAndServices(): string[] {
    return companyProfileOfferingItems(this.company?.company_overview);
  }

  get accountant(): string {
    return this.company?.financials?.financial_providers?.accountant || '';
  }

  get auditor(): string {
    return this.company?.financials?.financial_providers?.auditor || '';
  }

  // Domain used for the hero logo tile, preferring the canonical domain as the Logistics profile does.
  get logoDomain(): string {
    const overview = this.company?.company_overview;
    const domain = String(overview?.domain || '').trim();
    const url = companyProfileSafeUrl(/^https?:\/\//i.test(domain) ? domain : domain && `https://${domain}`) || companyProfileSafeUrl(overview?.url);
    return url ? new URL(url).hostname : '';
  }

  get hasRecordedEnergy(): boolean {
    if (!this.hasEnergyAccess) return false;
    return (this.company?.energy_and_emissions?.energy || []).some((item: any) =>
      item.total_energy_kwh != null || item.electric_kwh != null || item.gas_kwh != null);
  }

  get visibleHiring(): Record<string, any>[] {
    return (this.company?.hiring_for || []).slice(0, 3);
  }

  get visibleGrants(): Record<string, any>[] {
    return (this.company?.grants || []).slice(0, 3);
  }

  get visiblePatents(): Record<string, any>[] {
    return this.hasPatentAccess ? (this.company?.patents_filed || []).slice(0, 3) : [];
  }

  get hasActivity(): boolean {
    return !!(this.articles.length || this.visibleHiring.length || this.visibleGrants.length || this.visiblePatents.length);
  }

  externalUrl(value: unknown): string | undefined {
    return taxExternalUrl(value);
  }

  makeDefaultView(): void {
    if (this.savingDefault) return;
    this.savingDefault = true;
    this.authenticationService.setDefaultSkin('tax_accountancy').subscribe({
      next: () => {
        this.savingDefault = false;
        this.defaultSaved = true;
        this.pubMethods.showInfoMessage('Tax & accountancy is now your default view.');
      },
      error: () => {
        this.savingDefault = false;
        this.pubMethods.showInfoMessage('Could not save your default view. Please try again.');
      },
    });
  }

  openFullProfile(): void {
    this.authenticationService.setDefaultSkin('full').subscribe({
      next: () => this.router.navigate(['/profile', this.companyNumber], {
        queryParams: this.route.snapshot.queryParams,
        fragment: this.route.snapshot.fragment,
      }),
      error: () => this.pubMethods.showInfoMessage('Could not open the full profile. Please try again.'),
    });
  }
}
